/*
  Free caffeine calculator (sections/nr-caffeine-tool.liquid).

  Each drink's caffeine rises over about 45 minutes as it's absorbed, then
  halves every half-life. The page adds every drink together to draw the day,
  read the level at bedtime, and work out the latest time a reference coffee
  can be finished and still fall to the bedtime level by bed.

  The visitor's day is kept in localStorage on their own device only, so it
  is still there if they come back later the same day.
*/
if (!customElements.get('nr-caffeine-tool')) {
  customElements.define(
    'nr-caffeine-tool',
    class NrCaffeineTool extends HTMLElement {
      connectedCallback() {
        const configElement = this.querySelector('[data-config]');
        if (!configElement) return;
        try {
          this.config = JSON.parse(configElement.textContent);
        } catch (error) {
          return;
        }
        this.drinks = new Map(this.config.drinks.map((drink) => [drink.id, drink]));
        if (this.drinks.size === 0) return;

        this.el = {
          app: this.querySelector('[data-app]'),
          bedtime: this.querySelector('[data-bedtime]'),
          speed: this.querySelector('[data-speed]'),
          pregnant: this.querySelector('[data-pregnant]'),
          drink: this.querySelector('[data-drink]'),
          time: this.querySelector('[data-time]'),
          add: this.querySelector('[data-add]'),
          list: this.querySelector('[data-list]'),
          empty: this.querySelector('[data-empty]'),
          total: this.querySelector('[data-total]'),
          bedLevel: this.querySelector('[data-bed-level]'),
          latest: this.querySelector('[data-latest]'),
          meter: this.querySelector('[data-meter]'),
          notes: this.querySelector('[data-notes]'),
          chart: this.querySelector('[data-chart]'),
        };

        this.restore();
        this.bind();
        this.render();
        this.el.app.classList.add('is-ready');
      }

      /* ---------- State ---------- */

      restore() {
        this.entries = [];
        let saved = null;
        try {
          saved = JSON.parse(window.localStorage.getItem(NrCaffeineTool.storageKey) || 'null');
        } catch (error) {
          saved = null;
        }

        // Only keep a saved day from today; yesterday's drinks don't count.
        if (saved && saved.date === NrCaffeineTool.today()) {
          if (saved.bedtime) this.el.bedtime.value = saved.bedtime;
          if (saved.speed) this.el.speed.value = saved.speed;
          this.el.pregnant.checked = Boolean(saved.pregnant);
          (saved.entries || []).forEach((entry) => {
            if (this.drinks.has(entry.id) && NrCaffeineTool.minutes(entry.time) !== null) {
              this.entries.push({ id: entry.id, time: entry.time });
            }
          });
        } else {
          // First visit: start with one example drink so the chart shows how it works.
          this.entries.push({ id: this.config.drinks[0].id, time: '08:00' });
        }
      }

      save() {
        try {
          window.localStorage.setItem(
            NrCaffeineTool.storageKey,
            JSON.stringify({
              date: NrCaffeineTool.today(),
              bedtime: this.el.bedtime.value,
              speed: this.el.speed.value,
              pregnant: this.el.pregnant.checked,
              entries: this.entries,
            })
          );
        } catch (error) {
          /* Private browsing or storage turned off: the tool still works. */
        }
      }

      bind() {
        this.el.add.addEventListener('click', () => {
          const time = this.el.time.value;
          if (NrCaffeineTool.minutes(time) === null) {
            this.el.time.focus();
            return;
          }
          this.entries.push({ id: this.el.drink.value, time: time });
          this.track();
          this.update();
        });

        this.el.list.addEventListener('click', (event) => {
          const button = event.target.closest('[data-remove]');
          if (!button) return;
          this.entries.splice(Number(button.dataset.remove), 1);
          this.update();
        });

        this.el.pregnant.addEventListener('change', () => {
          if (this.el.pregnant.checked) this.el.speed.value = 'slow';
          this.update();
        });

        [this.el.bedtime, this.el.speed].forEach((input) => {
          input.addEventListener('change', () => this.update());
        });

        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-nr-tool-product]');
          if (link) NrCaffeineTool.publish('nr_tool_product_click', { tool: 'caffeine', product: link.dataset.nrToolProduct });
        });
      }

      update() {
        this.save();
        this.render();
      }

      track() {
        if (this.tracked) return;
        this.tracked = true;
        NrCaffeineTool.publish('nr_tool_use', { tool: 'caffeine' });
      }

      /* ---------- Maths ---------- */

      halfLife() {
        const value = Number(this.config.halfLife[this.el.speed.value]);
        return value > 0 ? value : 5;
      }

      // Minutes from midnight on a day that runs 5am to 5am, so a drink or a
      // bedtime after midnight lands after the evening, not before breakfast.
      static dayMinutes(time) {
        const value = NrCaffeineTool.minutes(time);
        if (value === null) return null;
        return value < 300 ? value + 1440 : value;
      }

      // Bedtimes before noon are taken as after midnight.
      bedMinutes() {
        const value = NrCaffeineTool.minutes(this.el.bedtime.value);
        if (value === null) return 22.5 * 60;
        return value < 720 ? value + 1440 : value;
      }

      timeline() {
        return this.entries
          .map((entry) => ({
            at: NrCaffeineTool.dayMinutes(entry.time),
            mg: Number(this.drinks.get(entry.id).mg) || 0,
          }))
          .filter((item) => item.at !== null);
      }

      static level(items, at, halfLife) {
        let total = 0;
        items.forEach((item) => {
          const hours = (at - item.at) / 60;
          if (hours <= 0) return;
          const absorbed = Math.min(1, hours / 0.75);
          total += item.mg * absorbed * Math.pow(0.5, hours / halfLife);
        });
        return total;
      }

      /* ---------- Drawing ---------- */

      render() {
        const pregnant = this.el.pregnant.checked;
        const halfLife = this.halfLife();
        const bed = this.bedMinutes();
        const items = this.timeline();
        const total = items.reduce((sum, item) => sum + item.mg, 0);
        const largest = items.reduce((max, item) => Math.max(max, item.mg), 0);
        const atBed = NrCaffeineTool.level(items, bed, halfLife);
        const limit = Number(pregnant ? this.config.limitPregnant : this.config.limitAdult);
        const text = this.config.text;

        this.renderList();

        this.el.total.textContent = Math.round(total);
        this.el.bedLevel.textContent = Math.round(atBed);

        // Latest time to finish the reference coffee so it falls to the
        // bedtime level by bed (other drinks aside).
        const ratio = Number(this.config.referenceMg) / Number(this.config.bedLevel);
        const latest = ratio > 1 ? bed - halfLife * 60 * Math.log2(ratio) : bed;
        const latestRounded = Math.floor(latest / 15) * 15;
        // Before 6am there's no sensible coffee time to show.
        const noLatest = latestRounded < 6 * 60;
        this.el.latest.textContent = noLatest ? '–' : NrCaffeineTool.clock(latestRounded);

        const share = limit > 0 ? Math.min(100, (total / limit) * 100) : 0;
        this.el.meter.firstElementChild.style.width = share + '%';
        this.el.meter.classList.toggle('nr-tool__meter--over', total > limit);

        const notes = [];
        if (items.length > 0) {
          const limitText = total > limit
            ? (pregnant ? text.overLimitPregnant : text.overLimit)
            : (pregnant ? text.underLimitPregnant : text.underLimit);
          notes.push({ text: NrCaffeineTool.fill(limitText, { limit: limit }), warning: total > limit });

          if (!pregnant && largest > Number(this.config.limitSingle)) {
            notes.push({
              text: NrCaffeineTool.fill(text.single, { mg: Math.round(largest), single: this.config.limitSingle }),
              warning: true,
            });
          }

          const high = atBed > Number(this.config.bedLevel);
          notes.push({ text: NrCaffeineTool.fill(high ? text.bedHigh : text.bedLow, { mg: Math.round(atBed) }), warning: high });
        }
        notes.push(
          noLatest
            ? { text: NrCaffeineTool.fill(text.latestNone, { mg: this.config.bedLevel }), warning: true }
            : { text: NrCaffeineTool.fill(text.latestPast, { time: NrCaffeineTool.clock(latestRounded) }), warning: false }
        );

        this.el.notes.replaceChildren(
          ...notes
            .filter((note) => note.text)
            .map((note) => {
              const li = document.createElement('li');
              li.textContent = note.text;
              if (note.warning) li.className = 'is-warning';
              return li;
            })
        );

        this.renderChart(items, bed, halfLife);
      }

      renderList() {
        const order = this.entries
          .map((entry, index) => ({ entry: entry, index: index, at: NrCaffeineTool.dayMinutes(entry.time) }))
          .sort((a, b) => a.at - b.at);

        this.el.list.replaceChildren(
          ...order.map((row) => {
            const drink = this.drinks.get(row.entry.id);
            const li = document.createElement('li');

            const time = document.createElement('span');
            time.className = 'nr-tool__list-time';
            time.textContent = row.entry.time;

            const name = document.createElement('span');
            name.className = 'nr-tool__list-name';
            name.textContent = drink.name;

            const mg = document.createElement('span');
            mg.className = 'nr-tool__list-mg';
            mg.textContent = drink.mg + ' mg';

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'nr-tool__remove';
            remove.dataset.remove = row.index;
            remove.setAttribute('aria-label', this.config.text.remove + ': ' + drink.name + ', ' + row.entry.time);
            remove.textContent = '×';

            li.append(time, name, mg, remove);
            return li;
          })
        );
        this.el.empty.hidden = this.entries.length > 0;
      }

      renderChart(items, bed, halfLife) {
        const ns = 'http://www.w3.org/2000/svg';
        const width = 640;
        const height = 240;
        const pad = { left: 44, right: 16, top: 18, bottom: 30 };
        const first = items.reduce((min, item) => Math.min(min, item.at), 6 * 60);
        const start = Math.floor(Math.min(6 * 60, first) / 60) * 60;
        const end = Math.max(bed + 120, start + 18 * 60);

        const points = [];
        let peak = 0;
        for (let at = start; at <= end; at += 10) {
          const value = NrCaffeineTool.level(items, at, halfLife);
          peak = Math.max(peak, value);
          points.push([at, value]);
        }
        const bedLevel = Number(this.config.bedLevel);
        const top = Math.max(50, Math.ceil(Math.max(peak, bedLevel * 2) / 50) * 50);

        const x = (at) => pad.left + ((at - start) / (end - start)) * (width - pad.left - pad.right);
        const y = (mg) => height - pad.bottom - (mg / top) * (height - pad.top - pad.bottom);

        const svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', this.config.text.chartLabel);

        const add = (tag, attributes, content) => {
          const node = document.createElementNS(ns, tag);
          Object.keys(attributes).forEach((key) => node.setAttribute(key, attributes[key]));
          if (content !== undefined) node.textContent = content;
          svg.appendChild(node);
          return node;
        };

        [0, top / 2, top].forEach((mg) => {
          add('line', { x1: pad.left, x2: width - pad.right, y1: y(mg), y2: y(mg), class: 'nr-chart__grid' });
          add('text', { x: pad.left - 8, y: y(mg) + 4, 'text-anchor': 'end', class: 'nr-chart__text' }, Math.round(mg) + ' mg');
        });

        for (let hour = Math.ceil(start / 180) * 180; hour <= end; hour += 180) {
          add('text', { x: x(hour), y: height - 8, 'text-anchor': 'middle', class: 'nr-chart__text' }, NrCaffeineTool.clock(hour));
        }

        const line = points.map((point, index) => (index ? 'L' : 'M') + x(point[0]).toFixed(1) + ' ' + y(point[1]).toFixed(1)).join(' ');
        add('path', { d: line + ' L' + x(end) + ' ' + y(0) + ' L' + x(start) + ' ' + y(0) + ' Z', class: 'nr-chart__area' });
        add('path', { d: line, class: 'nr-chart__line' });

        add('line', { x1: pad.left, x2: width - pad.right, y1: y(bedLevel), y2: y(bedLevel), class: 'nr-chart__level' });
        add('text', { x: pad.left + 6, y: y(bedLevel) - 6, class: 'nr-chart__text' }, this.config.text.chartLine);

        add('line', { x1: x(bed), x2: x(bed), y1: pad.top, y2: y(0), class: 'nr-chart__bed' });
        add('text', { x: x(bed) + 6, y: pad.top + 10, class: 'nr-chart__text nr-chart__text--strong' }, this.config.text.chartBed + ' ' + NrCaffeineTool.clock(bed));

        this.el.chart.replaceChildren(svg);
      }

      /* ---------- Helpers ---------- */

      static get storageKey() {
        return 'nr-caffeine-tool';
      }

      static today() {
        const now = new Date();
        return now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate();
      }

      // "08:30" to minutes after midnight, or null.
      static minutes(time) {
        const match = /^(\d{1,2}):(\d{2})/.exec(time || '');
        if (!match) return null;
        const hours = Number(match[1]);
        const minutes = Number(match[2]);
        if (hours > 23 || minutes > 59) return null;
        return hours * 60 + minutes;
      }

      // Minutes (possibly past midnight, or before it) to "08:30".
      static clock(value) {
        const minutes = ((Math.round(value) % 1440) + 1440) % 1440;
        return String(Math.floor(minutes / 60)).padStart(2, '0') + ':' + String(minutes % 60).padStart(2, '0');
      }

      static fill(template, values) {
        return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
      }

      static publish(name, detail) {
        try {
          if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail);
        } catch (error) {
          /* Tracking must never break the tool. */
        }
      }
    }
  );
}
